import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslatePipe, TranslationService } from '@kirigami/shared/i18n';

import { sanitizeHtmlWithLineBreaks } from '../utils/sanitize-html.helper';

/** The small icon before a point in the panel. */
type BulletIcon =
  | 'cards'
  | 'notes'
  | 'saveFor'
  | 'notebook'
  | 'ask'
  | 'verify'
  | 'literature'
  | 'topics'
  | 'at'
  | 'shared'
  | 'share'
  | 'tasks';

/** One screenshot in the panel's frame, its description resolved for the locale. */
interface FeatureShot {
  readonly src: string;
  readonly alt: string;
}

/** One feature of the tour, its copy resolved for the active locale. */
interface FeatureSlide {
  /** The area of the product, as the list names it. */
  readonly name: string;
  /** Sanitized so a point can carry a bold lead-in. */
  readonly bullets: readonly { readonly html: SafeHtml; readonly icon: BulletIcon }[];
  readonly shots: readonly FeatureShot[];
}

/**
 * The features in order, one per area of the product (owner 2026-11-03: Write,
 * Read, Search, Library, Collaboration), each pointing at its copy, its points'
 * icons and its screenshots, taken from the owner's dev account with every
 * text from users and papers blurred (owner 2026-11-04). The keys are spelled out
 * rather than built from a slug so the compiler checks every one of them.
 */
const STAGE_SLIDES = [
  {
    name: 'landing.stage.slides.write.name',
    bullets: [
      { key: 'landing.stage.slides.write.bullets.feedback', icon: 'cards' },
      { key: 'landing.stage.slides.write.bullets.method', icon: 'verify' },
      { key: 'landing.stage.slides.write.bullets.reanalyse', icon: 'notes' },
    ],
    shots: [
      { src: 'img/landing-write-overview.webp', alt: 'landing.stage.slides.write.shots.overview' },
      { src: 'img/landing-write-card.webp', alt: 'landing.stage.slides.write.shots.card' },
    ],
  },
  {
    name: 'landing.stage.slides.read.name',
    bullets: [
      { key: 'landing.stage.slides.read.bullets.notes', icon: 'notes' },
      { key: 'landing.stage.slides.read.bullets.saveFor', icon: 'saveFor' },
      { key: 'landing.stage.slides.read.bullets.notebook', icon: 'notebook' },
    ],
    shots: [
      { src: 'img/landing-read-notes.webp', alt: 'landing.stage.slides.read.shots.notes' },
      { src: 'img/landing-read-in-write.webp', alt: 'landing.stage.slides.read.shots.inWrite' },
      { src: 'img/landing-read-notebook.webp', alt: 'landing.stage.slides.read.shots.notebook' },
    ],
  },
  {
    name: 'landing.stage.slides.search.name',
    bullets: [
      { key: 'landing.stage.slides.search.bullets.ask', icon: 'ask' },
      { key: 'landing.stage.slides.search.bullets.verify', icon: 'verify' },
      { key: 'landing.stage.slides.search.bullets.literature', icon: 'literature' },
    ],
    shots: [
      { src: 'img/landing-search-verify.webp', alt: 'landing.stage.slides.search.shots.verify' },
      { src: 'img/landing-search-sources.webp', alt: 'landing.stage.slides.search.shots.sources' },
      {
        src: 'img/landing-search-literature.webp',
        alt: 'landing.stage.slides.search.shots.literature',
      },
    ],
  },
  {
    name: 'landing.stage.slides.library.name',
    bullets: [
      { key: 'landing.stage.slides.library.bullets.topics', icon: 'topics' },
      { key: 'landing.stage.slides.library.bullets.papers', icon: 'at' },
      { key: 'landing.stage.slides.library.bullets.shared', icon: 'shared' },
    ],
    shots: [
      { src: 'img/landing-library-topics.webp', alt: 'landing.stage.slides.library.shots.topics' },
      { src: 'img/landing-library-new.webp', alt: 'landing.stage.slides.library.shots.new' },
      { src: 'img/landing-library-papers.webp', alt: 'landing.stage.slides.library.shots.papers' },
    ],
  },
  {
    name: 'landing.stage.slides.collaborate.name',
    bullets: [
      { key: 'landing.stage.slides.collaborate.bullets.share', icon: 'share' },
      { key: 'landing.stage.slides.collaborate.bullets.tasks', icon: 'tasks' },
      { key: 'landing.stage.slides.collaborate.bullets.lab', icon: 'shared' },
    ],
    shots: [
      { src: 'img/landing-team-share.webp', alt: 'landing.stage.slides.collaborate.shots.share' },
      { src: 'img/landing-team-tasks.webp', alt: 'landing.stage.slides.collaborate.shots.tasks' },
      { src: 'img/landing-team-lab.webp', alt: 'landing.stage.slides.collaborate.shots.lab' },
    ],
  },
] as const;

/** How long one screenshot shows while the tour plays: a feature lasts one of these per screenshot. */
export const FEATURE_SHOT_MS = 3400;

/** The tour's clock. Fine enough for the progress line to move smoothly. */
const TICK_MS = 100;

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/**
 * Product feature tour, after the owner's reference (veritus.ai, 2026-11-03),
 * made calmer on her review the same day ("muito texto", titles in blue): the
 * areas as a list of blue titles on the left; the one shown lifts into a white
 * card that opens its three points, with a line under them that fills while it
 * shows; on the right, only its screenshots, large. Until 2026-11-03 the page
 * held the scroll on every screenshot, which the owner dropped: the section
 * scrolls like any other now.
 *
 * The tour plays on its own while it is on screen, one area after the other,
 * and holds while the pointer or the keyboard focus is on it. Picking an area
 * stops it for good: the visitor has taken over. The pause button stops and
 * restarts it (WCAG 2.2.2), and with reduced motion it never plays. Each title
 * is a disclosure button (`aria-expanded`) for its points; on a small screen the
 * screenshots sit inside the open card, under its points.
 */
@Component({
  selector: 'app-feature-stage-widget',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, TranslatePipe],
  template: `
    <section id="features" class="tour" [attr.aria-label]="'landing.stage.sectionAria' | t">
      <h2 class="tour__sr">{{ 'landing.stage.tablistAria' | t }}</h2>
      <div
        class="tour__grid"
        (mouseenter)="hovering.set(true)"
        (mouseleave)="hovering.set(false)"
        (focusin)="focused.set(true)"
        (focusout)="onFocusOut($event)"
      >
        <div class="tour__side">
          <ul class="tour__list">
            @for (slide of slides(); track slide.name; let i = $index) {
              <li class="tour__item" [class.tour__item--on]="i === active()">
                <h3 class="tour__item-h">
                  <button
                    type="button"
                    class="tour__tab"
                    [id]="'tour-tab-' + i"
                    [attr.aria-expanded]="i === active()"
                    [attr.aria-controls]="'tour-detail-' + i"
                    (click)="pick(i)"
                  >
                    {{ slide.name }}
                  </button>
                </h3>
                @if (i === active()) {
                  <div class="tour__detail" [id]="'tour-detail-' + i">
                    <ul class="tour__points">
                      @for (bullet of slide.bullets; track $index) {
                        <li class="tour__point">
                          <span class="tour__icon" aria-hidden="true">
                            @switch (bullet.icon) {
                              @case ('at') {
                                <span class="tour__glyph">&#64;</span>
                              }
                              @default {
                                <svg viewBox="0 0 24 24">
                                  <path [attr.d]="iconPath(bullet.icon)" />
                                </svg>
                              }
                            }
                          </span>
                          <span class="tour__point-text" [innerHTML]="bullet.html"></span>
                        </li>
                      }
                    </ul>
                    <div class="tour__media tour__media--inline">
                      <ng-container
                        *ngTemplateOutlet="mediaTpl; context: { $implicit: slide }"
                      ></ng-container>
                    </div>
                    <span class="tour__line" aria-hidden="true">
                      <span
                        class="tour__line-fill"
                        [style.transform]="'scaleX(' + progress() + ')'"
                      ></span>
                    </span>
                  </div>
                }
              </li>
            }
          </ul>
          @if (!reducedMotion() && !stopped()) {
            <button
              type="button"
              class="tour__pause"
              [attr.aria-label]="(paused() ? 'landing.stage.play' : 'landing.stage.pause') | t"
              [attr.aria-pressed]="paused()"
              (click)="paused.set(!paused())"
            >
              @if (paused()) {
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M8 5.5v13l10.5-6.5L8 5.5Z" />
                </svg>
              } @else {
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M8.5 5.5v13M15.5 5.5v13" />
                </svg>
              }
            </button>
          }
        </div>

        <div class="tour__media tour__media--side">
          <ng-container
            *ngTemplateOutlet="mediaTpl; context: { $implicit: current() }"
          ></ng-container>
        </div>
      </div>
    </section>

    <!-- A window around the screenshots, then a dot per screenshot to pick one. -->
    <ng-template #mediaTpl let-feat>
      <div class="tour__frame">
        <div class="tour__bar" aria-hidden="true"><i></i><i></i><i></i></div>
        <div class="tour__shot">
          @for (still of feat.shots; track still.src; let s = $index) {
            <img
              class="tour__still"
              [class.tour__still--on]="s === shot()"
              [src]="still.src"
              [alt]="s === shot() ? still.alt : ''"
              [attr.aria-hidden]="s === shot() ? null : true"
              width="1704"
              height="1012"
              [attr.loading]="active() === 0 && s === 0 ? null : 'lazy'"
              decoding="async"
            />
          }
        </div>
      </div>
      @if (feat.shots.length > 1) {
        <div class="tour__dots">
          @for (dot of feat.shots; track dot.src; let s = $index) {
            <button
              type="button"
              class="tour__dot"
              [attr.aria-label]="
                'landing.stage.shotLabel' | t: { n: s + 1, total: feat.shots.length }
              "
              [attr.aria-current]="s === shot() ? 'true' : null"
              (click)="pickShot(s)"
            ></button>
          }
        </div>
      }
    </ng-template>
  `,
  styles: [
    `
      .tour {
        padding: 6rem 1.5rem;
        background: var(--neu-0);
      }
      .tour__sr {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip-path: inset(50%);
        white-space: nowrap;
      }
      .tour__grid {
        max-width: 85rem;
        margin: 0 auto;
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 1.5rem;
      }
      @media (min-width: 1024px) {
        .tour {
          padding: 7.5rem 4.0625rem;
        }
        .tour__grid {
          grid-template-columns: minmax(18rem, 0.9fr) minmax(0, 1.9fr);
          gap: 3.5rem;
          align-items: center;
        }
      }

      /* ── The list ──────────────────────────────────────────── */
      .tour__side {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }
      .tour__list {
        margin: 0;
        padding: 0;
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }
      /* Shown, the area lifts into a white card that opens its points; the
         others rest on the page as a title. */
      .tour__item {
        border: 1px solid transparent;
        border-radius: 1rem;
        transition:
          background-color 0.25s ease,
          border-color 0.25s ease,
          box-shadow 0.25s ease;
      }
      .tour__item--on {
        border-color: var(--neu-100);
        background: var(--neu-0);
        box-shadow: 0 12px 32px rgba(34, 39, 43, 0.07);
      }
      .tour__item-h {
        margin: 0;
      }
      /* The titles in the brand's blue (owner 2026-11-03): kirigami-600 at rest
         (5.9:1 on white), kirigami-700 when shown (7.6:1). */
      .tour__tab {
        display: block;
        width: 100%;
        padding: 1rem 1.5rem;
        border: 0;
        border-radius: 1rem;
        background: transparent;
        font: inherit;
        font-size: 1.375rem;
        font-weight: 700;
        line-height: 1.25;
        letter-spacing: -0.011em;
        text-align: left;
        color: var(--kirigami-600);
        cursor: pointer;
        transition: color 0.25s ease;

        &:hover {
          color: var(--kirigami-700);
        }

        &:focus-visible {
          outline: 2px solid var(--kirigami-600);
          outline-offset: 2px;
        }
      }
      .tour__item--on .tour__tab {
        padding-bottom: 0.5rem;
        color: var(--kirigami-700);
      }
      .tour__detail {
        padding: 0 1.5rem 1.5rem;
      }
      .tour__points {
        margin: 0.5rem 0 0;
        padding: 0;
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 0.875rem;
      }
      .tour__point {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        font-size: 1rem;
        line-height: 1.5;
        color: var(--neu-800);
      }
      /* The points are bound as HTML, so a bold lead-in carries no scoping
         attribute of its own. */
      .tour__point-text ::ng-deep strong {
        font-weight: 600;
        color: var(--neu-950);
      }
      .tour__icon {
        flex: none;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1.5rem;
        height: 1.5rem;
        color: var(--kirigami-600);

        svg {
          width: 1.25rem;
          height: 1.25rem;
          fill: none;
          stroke: currentColor;
          stroke-width: 1.75;
          stroke-linecap: round;
          stroke-linejoin: round;
        }
      }
      .tour__glyph {
        font-size: 1.125rem;
        font-weight: 700;
        line-height: 1;
      }
      /* The line that fills while the area shows: full once the tour has
         stopped. kirigami-700 is 7.6:1 on white. */
      .tour__line {
        display: block;
        height: 2px;
        margin-top: 1.25rem;
        border-radius: 999px;
        overflow: hidden;
      }
      .tour__line-fill {
        display: block;
        height: 100%;
        background: var(--kirigami-700);
        transform-origin: left center;
      }
      .tour__pause {
        align-self: flex-start;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.5rem;
        height: 2.5rem;
        margin-left: 1rem;
        border: 1px solid var(--neu-400);
        border-radius: 999px;
        background: var(--neu-0);
        color: var(--neu-800);
        cursor: pointer;

        svg {
          width: 1.125rem;
          height: 1.125rem;
          fill: currentColor;
          stroke: currentColor;
          stroke-width: 2;
          stroke-linecap: round;
          stroke-linejoin: round;
        }

        &:hover {
          border-color: var(--neu-600);
          color: var(--neu-950);
        }

        &:focus-visible {
          outline: 2px solid var(--kirigami-600);
          outline-offset: 2px;
        }
      }

      /* ── The screenshots ───────────────────────────────────── */
      /* Beside the list on a wide screen; inside the open card on a small one. */
      .tour__media--side {
        display: none;
      }
      .tour__media--inline {
        margin-top: 1.25rem;
      }
      @media (min-width: 1024px) {
        .tour__media--side {
          display: block;
        }
        .tour__media--inline {
          display: none;
        }
      }
      /* A window around the screenshot: a light bar with its three dots, the
         product below. */
      .tour__frame {
        overflow: hidden;
        border: 1px solid var(--neu-200);
        border-radius: 1rem;
        background: var(--neu-50);
        box-shadow: 0 24px 64px rgba(34, 39, 43, 0.12);
      }
      .tour__bar {
        display: flex;
        gap: 0.375rem;
        padding: 0.625rem 0.75rem;
        border-bottom: 1px solid var(--neu-100);
        background: var(--neu-100);

        i {
          width: 0.5625rem;
          height: 0.5625rem;
          border-radius: 999px;
          background: var(--neu-300);
        }
        i:nth-child(1) {
          background: #ec6a5e;
        }
        i:nth-child(2) {
          background: #f4bf4f;
        }
        i:nth-child(3) {
          background: #61c554;
        }
      }
      .tour__shot {
        position: relative;
        aspect-ratio: 1704 / 1012;
      }
      .tour__still {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: top left;
        opacity: 0;
        transition: opacity 0.6s ease;
      }
      .tour__still--on {
        opacity: 1;
      }
      .tour__dots {
        display: flex;
        justify-content: center;
        gap: 0.25rem;
        margin-top: 0.875rem;
      }
      /* A 24px target around an 8px dot (WCAG 2.5.8). */
      .tour__dot {
        width: 1.5rem;
        height: 1.5rem;
        padding: 0.5rem;
        border: 0;
        border-radius: 999px;
        background: var(--neu-300);
        background-clip: content-box;
        cursor: pointer;

        &[aria-current='true'] {
          background-color: var(--kirigami-700);
        }

        &:focus-visible {
          outline: 2px solid var(--kirigami-600);
          outline-offset: 0;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .tour__item,
        .tour__tab,
        .tour__still {
          transition: none;
        }
      }
    `,
  ],
})
export class FeatureStageWidgetComponent implements AfterViewInit, OnDestroy {
  private readonly translations = inject(TranslationService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** The feature shown. */
  readonly active = signal(0);
  /** How long the shown feature has been playing. */
  readonly elapsed = signal(0);
  /** The screenshot shown when the visitor picked one. */
  readonly #pickedShot = signal<number | null>(null);

  readonly hovering = signal(false);
  readonly focused = signal(false);
  readonly paused = signal(false);
  /** The visitor picked a feature: the tour does not play again on this visit. */
  readonly stopped = signal(false);
  readonly reducedMotion = signal(false);
  /** The section is on screen (an observer says so; without one, always). */
  readonly inView = signal(false);
  readonly #pageVisible = signal(true);

  private timer?: ReturnType<typeof setInterval>;
  private observer?: IntersectionObserver;
  private motionQuery?: MediaQueryList;
  private readonly onMotionChange = (event: MediaQueryListEvent) =>
    this.reducedMotion.set(event.matches);
  private readonly onVisibility = () =>
    this.#pageVisible.set(document.visibilityState === 'visible');

  /**
   * The features with their copy resolved for the active locale. Resolved
   * here rather than in the template because a `@for` cannot name a key
   * literally.
   */
  readonly slides = computed<readonly FeatureSlide[]>(() =>
    STAGE_SLIDES.map((slide) => ({
      name: this.translations.t(slide.name),
      bullets: slide.bullets.map((bullet) => ({
        html: sanitizeHtmlWithLineBreaks(this.sanitizer, this.translations.t(bullet.key)),
        icon: bullet.icon,
      })),
      shots: slide.shots.map((shot) => ({ src: shot.src, alt: this.translations.t(shot.alt) })),
    })),
  );

  readonly current = computed(() => this.slides()[this.active()]);

  /** The tour is moving: on screen, not held, not stopped, motion allowed. */
  readonly playing = computed(
    () =>
      !this.reducedMotion() &&
      !this.stopped() &&
      !this.paused() &&
      !this.hovering() &&
      !this.focused() &&
      this.inView() &&
      this.#pageVisible(),
  );

  /** How long the shown feature plays: one {@link FEATURE_SHOT_MS} per screenshot. */
  readonly #duration = computed(() => this.current().shots.length * FEATURE_SHOT_MS);

  /** How far the line under the shown feature has filled (0..1); full once the tour is done with it. */
  readonly progress = computed(() =>
    this.stopped() || this.reducedMotion() ? 1 : Math.min(1, this.elapsed() / this.#duration()),
  );

  /** The screenshot shown: the one picked, else the one the clock has reached. */
  readonly shot = computed(() => {
    const picked = this.#pickedShot();
    if (picked !== null) return picked;
    const last = this.current().shots.length - 1;
    return Math.min(last, Math.floor(this.elapsed() / FEATURE_SHOT_MS));
  });

  ngAfterViewInit(): void {
    if (typeof window === 'undefined') return;
    if (typeof matchMedia !== 'undefined') {
      this.motionQuery = matchMedia(REDUCED_MOTION);
      this.reducedMotion.set(this.motionQuery.matches);
      this.motionQuery.addEventListener('change', this.onMotionChange);
    }
    document.addEventListener('visibilitychange', this.onVisibility);
    const section = this.host.nativeElement.querySelector('.tour');
    if (section && typeof IntersectionObserver !== 'undefined') {
      this.observer = new IntersectionObserver(
        (entries) => this.inView.set(entries.some((entry) => entry.isIntersecting)),
        { threshold: 0.35 },
      );
      this.observer.observe(section);
    } else {
      this.inView.set(true);
    }
    this.timer = setInterval(() => this.tick(), TICK_MS);
  }

  ngOnDestroy(): void {
    if (this.timer) clearInterval(this.timer);
    this.observer?.disconnect();
    this.motionQuery?.removeEventListener('change', this.onMotionChange);
    if (typeof document !== 'undefined')
      document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /** One step of the clock: the line fills, the screenshots turn, then the next feature. */
  tick(): void {
    if (!this.playing()) return;
    const next = this.elapsed() + TICK_MS;
    if (next < this.#duration()) {
      this.elapsed.set(next);
      return;
    }
    this.active.set((this.active() + 1) % this.slides().length);
    this.elapsed.set(0);
    this.#pickedShot.set(null);
  }

  /** The visitor picked a feature: show it whole and stop the tour. */
  pick(index: number): void {
    this.stopped.set(true);
    this.#pickedShot.set(index === this.active() ? this.shot() : 0);
    this.active.set(index);
    this.elapsed.set(0);
  }

  pickShot(index: number): void {
    this.stopped.set(true);
    this.#pickedShot.set(index);
  }

  /** Focus leaving the tour lets it play again; moving inside it does not. */
  onFocusOut(event: FocusEvent): void {
    const to = event.relatedTarget as Node | null;
    if (!to || !this.host.nativeElement.contains(to)) this.focused.set(false);
  }

  iconPath(icon: BulletIcon): string {
    return ICON_PATHS[icon] ?? '';
  }
}

/** Stroke icons drawn on a 24-unit grid, one per kind of point. */
const ICON_PATHS: Partial<Record<BulletIcon, string>> = {
  cards:
    'M5 4.5h14a1.5 1.5 0 0 1 1.5 1.5v12a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 18V6A1.5 1.5 0 0 1 5 4.5ZM7 9h10M7 13h7',
  notes: 'M5 19.5h4.5L19 10a2.1 2.1 0 0 0-3-3l-9.5 9.5L5 19.5ZM14 8.5l2 2M4 22h16',
  saveFor: 'M6 3.5h8l4 4V20.5H6ZM14 3.5v4h4M9 13.5l2.5 2.5 4-4.5',
  notebook: 'M6 3.5h11a1.5 1.5 0 0 1 1.5 1.5v14a1.5 1.5 0 0 1-1.5 1.5H6ZM9 3.5v17M12 8h4M12 11.5h4',
  ask: 'M5 5h14a1.5 1.5 0 0 1 1.5 1.5v8A1.5 1.5 0 0 1 19 16h-7l-4.5 4v-4H5a1.5 1.5 0 0 1-1.5-1.5v-8A1.5 1.5 0 0 1 5 5Z',
  verify: 'M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17ZM8.5 12.2l2.4 2.4 4.6-5',
  literature:
    'M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17ZM3.5 12h17M12 3.5c2.4 2.4 3.5 5.2 3.5 8.5s-1.1 6.1-3.5 8.5c-2.4-2.4-3.5-5.2-3.5-8.5s1.1-6.1 3.5-8.5Z',
  topics:
    'M3.5 7.5a1.5 1.5 0 0 1 1.5-1.5h4.5l2 2.5H19a1.5 1.5 0 0 1 1.5 1.5v8A1.5 1.5 0 0 1 19 19.5H5A1.5 1.5 0 0 1 3.5 18Z',
  shared:
    'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM2.5 20c.6-3.4 3.2-5.5 6.5-5.5s5.9 2.1 6.5 5.5M16 4.3a3.5 3.5 0 0 1 0 6.4M18 14.8c1.9.8 3.1 2.6 3.5 5.2',
  share:
    'M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM3.5 20c.6-3.4 3.2-5.5 6.5-5.5 1.3 0 2.5.3 3.5.9M18.5 14v6M15.5 17h6',
  tasks: 'M10 6.5h10M10 12h10M10 17.5h10M3.5 6.5l1.5 1.5 2.5-3M3.5 12l1.5 1.5 2.5-3M4.5 17.5h2',
};
